import { useState, FormEvent } from 'react';
import type { User, UserCreatePayload, UserUpdatePayload } from '../services/users';
import type { Company } from '../services/companies';

interface UserFormProps {
  user?: User | null;
  companies: Company[];
  onSubmit: (data: UserCreatePayload | UserUpdatePayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const USER_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'admin', label: 'Admin' },
] as const;

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const;

export function UserForm({ user, companies, onSubmit, onCancel, isSubmitting = false }: UserFormProps) {
  const isEdit = !!user;

  const [name, setName] = useState(user?.name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [userType, setUserType] = useState<string>(user?.user_type ?? 'client');
  const [cellPhone, setCellPhone] = useState(user?.cell_phone ?? '');
  const [position, setPosition] = useState(user?.position ?? '');
  const [companyId, setCompanyId] = useState(user?.company_id ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(user?.date_of_birth ?? '');
  const [status, setStatus] = useState<string>(user?.status ?? 'active');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length > 50) {
      newErrors.name = 'Name must not exceed 50 characters';
    }

    if (!lastName.trim()) {
      newErrors.last_name = 'Last name is required';
    } else if (lastName.trim().length > 50) {
      newErrors.last_name = 'Last name must not exceed 50 characters';
    }

    if (!isEdit) {
      if (!email.trim()) {
        newErrors.email = 'Email is required';
      } else if (email.trim().length > 254) {
        newErrors.email = 'Email must not exceed 254 characters';
      } else {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(email.trim())) {
          newErrors.email = 'Email must be a valid email address';
        }
      }
    }

    if (!companyId) {
      newErrors.company_id = 'Company is required';
    }

    if (cellPhone) {
      if (!/^\d+$/.test(cellPhone)) {
        newErrors.cell_phone = 'Cell phone must contain only digits';
      } else if (cellPhone.length > 15) {
        newErrors.cell_phone = 'Cell phone must not exceed 15 digits';
      }
    }

    if (position && position.length > 100) {
      newErrors.position = 'Position must not exceed 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    if (isEdit) {
      const payload: UserUpdatePayload = {
        name: name.trim(),
        last_name: lastName.trim(),
        user_type: userType as UserUpdatePayload['user_type'],
        cell_phone: cellPhone || null,
        position: position || null,
        company_id: companyId,
        date_of_birth: dateOfBirth || null,
        status: status as UserUpdatePayload['status'],
      };
      await onSubmit(payload);
    } else {
      const payload: UserCreatePayload = {
        name: name.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        user_type: userType as UserCreatePayload['user_type'],
        cell_phone: cellPhone || null,
        position: position || null,
        company_id: companyId,
        date_of_birth: dateOfBirth || null,
        status: status as UserCreatePayload['status'],
      };
      await onSubmit(payload);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <h2>{isEdit ? 'Edit User' : 'Create User'}</h2>

      <div className="form__row">
        <div className="form__field">
          <label htmlFor="user_name">Name *</label>
          <input
            id="user_name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            required
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {errors.name && (
            <span id="name-error" className="form__error" role="alert">
              {errors.name}
            </span>
          )}
        </div>

        <div className="form__field">
          <label htmlFor="user_last_name">Last Name *</label>
          <input
            id="user_last_name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={50}
            required
            aria-invalid={!!errors.last_name}
            aria-describedby={errors.last_name ? 'last_name-error' : undefined}
          />
          {errors.last_name && (
            <span id="last_name-error" className="form__error" role="alert">
              {errors.last_name}
            </span>
          )}
        </div>
      </div>

      <div className="form__field">
        <label htmlFor="user_email">Email *</label>
        <input
          id="user_email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
          required
          disabled={isEdit}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : isEdit ? 'email-hint' : undefined}
        />
        {isEdit && (
          <span id="email-hint" className="form__hint">
            Email cannot be changed after creation
          </span>
        )}
        {errors.email && (
          <span id="email-error" className="form__error" role="alert">
            {errors.email}
          </span>
        )}
      </div>

      <div className="form__row">
        <div className="form__field">
          <label htmlFor="user_type">User Type *</label>
          <select
            id="user_type"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            required
          >
            {USER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form__field">
          <label htmlFor="user_status">Status *</label>
          <select
            id="user_status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            required
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form__field">
        <label htmlFor="user_company">Company *</label>
        <select
          id="user_company"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          required
          aria-invalid={!!errors.company_id}
          aria-describedby={errors.company_id ? 'company_id-error' : undefined}
        >
          <option value="">Select a company...</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.usual_name} ({c.client_code})
            </option>
          ))}
        </select>
        {errors.company_id && (
          <span id="company_id-error" className="form__error" role="alert">
            {errors.company_id}
          </span>
        )}
      </div>

      <div className="form__row">
        <div className="form__field">
          <label htmlFor="user_cell_phone">Cell Phone</label>
          <input
            id="user_cell_phone"
            type="text"
            value={cellPhone}
            onChange={(e) => setCellPhone(e.target.value)}
            maxLength={15}
            placeholder="Only digits"
            aria-invalid={!!errors.cell_phone}
            aria-describedby={errors.cell_phone ? 'cell_phone-error' : undefined}
          />
          {errors.cell_phone && (
            <span id="cell_phone-error" className="form__error" role="alert">
              {errors.cell_phone}
            </span>
          )}
        </div>

        <div className="form__field">
          <label htmlFor="user_position">Position</label>
          <input
            id="user_position"
            type="text"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            maxLength={100}
            aria-invalid={!!errors.position}
            aria-describedby={errors.position ? 'position-error' : undefined}
          />
          {errors.position && (
            <span id="position-error" className="form__error" role="alert">
              {errors.position}
            </span>
          )}
        </div>
      </div>

      <div className="form__field">
        <label htmlFor="user_dob">Date of Birth</label>
        <input
          id="user_dob"
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
        />
      </div>

      <div className="form__actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
